import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api';

const TestPage = ({ user, onLogout }) => {
  const { moduleId } = useParams();
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTest();
  }, [moduleId]);

  const fetchTest = async () => {
    try {
      const response = await api.get(`/tests/${moduleId}`);
      setTest(response.data);
      setAnswers(new Array(response.data.questions.length).fill(null));
    } catch (err) {
      console.error('Error fetching test:', err);
      alert('Test not found for this module');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionIndex, optionIndex) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = optionIndex;
    setAnswers(newAnswers);
  };

  const handleSubmit = async () => {
    // Check if all questions are answered
    if (answers.some(a => a === null)) {
      alert('Please answer all questions before submitting.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/tests/submit', {
        module_id: moduleId,
        answers: answers
      });
      setResult(response.data);
    } catch (err) {
      console.error('Error submitting test:', err);
      alert('Failed to submit test. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!test) {
    return null;
  }

  return (
    <div className="test-page" data-testid="test-page">
      <div className="dashboard-header">
        <h1>Module Test</h1>
        <div className="user-info">
          <Link to="/dashboard" className="btn btn-secondary" data-testid="back-btn">Back to Dashboard</Link>
          <button onClick={onLogout} className="btn-logout" data-testid="logout-btn">Logout</button>
        </div>
      </div>
      
      <div className="test-container">
        <div className="test-header">
          <h1>Module Test</h1>
          <p style={{ color: '#64748b', marginTop: '10px' }}>
            Passing Score: {test.passing_score}% | Total Questions: {test.questions.length}
          </p>
        </div>
        
        {test.questions.map((question, qIndex) => (
          <div key={qIndex} className="question-card" data-testid={`question-${qIndex + 1}`}>
            <div className="question-number">QUESTION {qIndex + 1} OF {test.questions.length}</div>
            <div className="question-text">{question.question_text}</div>
            
            <ul className="options-list">
              {question.options.map((option, oIndex) => (
                <li
                  key={oIndex}
                  className={`option-item ${answers[qIndex] === oIndex ? 'selected' : ''}`}
                  onClick={() => handleAnswerSelect(qIndex, oIndex)}
                  data-testid={`option-${qIndex}-${oIndex}`}
                >
                  <div className="option-radio"></div>
                  <span>{option}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
        
        <button 
          onClick={handleSubmit} 
          className="submit-test-btn" 
          disabled={submitting || answers.some(a => a === null)}
          data-testid="submit-test-btn"
        >
          {submitting ? 'Submitting...' : 'Submit Test'}
        </button>
      </div>
      
      {result && (
        <div className="modal-overlay" data-testid="result-modal">
          <div className="modal-content">
            <div className={`result-icon ${result.passed ? 'pass' : 'fail'}`}>
              {result.passed ? '✅' : '❌'}
            </div>
            <h2>{result.passed ? 'Congratulations!' : 'Not Quite There'}</h2>
            <p style={{ fontSize: '1.1rem', color: '#64748b', marginBottom: '20px' }}>
              {result.passed ? 'You passed the test!' : 'You need to retake this test.'}
            </p>
            
            <div className="result-stats" data-testid="result-stats">
              <p><strong>Your Score:</strong> {result.score}%</p>
              <p><strong>Correct Answers:</strong> {result.correct_answers} / {result.total_questions}</p>
              <p><strong>Passing Score:</strong> {result.passing_score}%</p>
            </div>
            
            <button onClick={handleClose} className="btn-full" data-testid="close-result-btn">
              {result.passed ? 'Continue to Dashboard' : 'Return to Dashboard'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestPage;
